/**
 * Vector stroke primitives used to author lessons as compact TypeScript.
 * Every function returns SVG path data (`d`) in the lesson's own coordinate system
 * (default viewBox 400 x 300, origin top-left). Numbers are rounded to 0.1 to keep the
 * shipped content small. Nothing here touches the DOM.
 */

export type Pt = readonly [number, number];

const r1 = (n: number) => Math.round(n * 10) / 10;
const fmt = (p: Pt) => `${r1(p[0])} ${r1(p[1])}`;
const rad = (deg: number) => (deg * Math.PI) / 180;

export const M = (p: Pt) => `M${fmt(p)}`;

/** Straight segment. */
export const line = (x1: number, y1: number, x2: number, y2: number) => `M${fmt([x1, y1])}L${fmt([x2, y2])}`;

/** Open or closed polyline through points. */
export function poly(points: Pt[], closed = false): string {
  if (!points.length) return '';
  return `M${fmt(points[0]!)}` + points.slice(1).map((p) => `L${fmt(p)}`).join('') + (closed ? 'Z' : '');
}

/**
 * Smooth curve through points (Catmull-Rom converted to cubic Béziers).
 * `tension` 0..1 (0.5 = classic Catmull-Rom).
 */
export function smooth(points: Pt[], closed = false, tension = 0.5): string {
  const n = points.length;
  if (n < 2) return '';
  if (n === 2) return poly(points);
  const k = tension / 3;
  const P = (i: number): Pt => (closed ? points[(i + n) % n]! : points[Math.max(0, Math.min(n - 1, i))]!);
  let d = `M${fmt(points[0]!)}`;
  const segs = closed ? n : n - 1;
  for (let i = 0; i < segs; i++) {
    const p0 = P(i - 1);
    const p1 = P(i);
    const p2 = P(i + 1);
    const p3 = P(i + 2);
    const c1: Pt = [p1[0] + (p2[0] - p0[0]) * k * 1.5, p1[1] + (p2[1] - p0[1]) * k * 1.5];
    const c2: Pt = [p2[0] - (p3[0] - p1[0]) * k * 1.5, p2[1] - (p3[1] - p1[1]) * k * 1.5];
    d += `C${fmt(c1)} ${fmt(c2)} ${fmt(p2)}`;
  }
  return d + (closed ? 'Z' : '');
}

/** Quadratic curve from a to b bending through control c. */
export const quad = (a: Pt, c: Pt, b: Pt) => `M${fmt(a)}Q${fmt(c)} ${fmt(b)}`;

/** Cubic curve. */
export const cubic = (a: Pt, c1: Pt, c2: Pt, b: Pt) => `M${fmt(a)}C${fmt(c1)} ${fmt(c2)} ${fmt(b)}`;

/** Point on an (optionally rotated) ellipse at angle `deg` (0 = right, 90 = down). */
export function ellipsePoint(cx: number, cy: number, rx: number, ry: number, deg: number, rotDeg = 0): Pt {
  const a = rad(deg);
  const x = rx * Math.cos(a);
  const y = ry * Math.sin(a);
  const r = rad(rotDeg);
  return [cx + x * Math.cos(r) - y * Math.sin(r), cy + x * Math.sin(r) + y * Math.cos(r)];
}

/**
 * Elliptical arc from angle a0 to a1 (degrees, clockwise on screen when a1 > a0), sampled as a
 * smooth curve so it animates evenly and can be rotated.
 */
export function arc(cx: number, cy: number, rx: number, ry: number, a0: number, a1: number, rotDeg = 0): string {
  const steps = Math.max(4, Math.ceil(Math.abs(a1 - a0) / 15));
  const pts: Pt[] = [];
  for (let i = 0; i <= steps; i++) pts.push(ellipsePoint(cx, cy, rx, ry, a0 + ((a1 - a0) * i) / steps, rotDeg));
  return smooth(pts);
}

/** Full ellipse drawn as one closed stroke starting at `start` degrees (default: top). */
export function ellipse(cx: number, cy: number, rx: number, ry: number, rotDeg = 0, start = -90): string {
  const pts: Pt[] = [];
  for (let i = 0; i < 24; i++) pts.push(ellipsePoint(cx, cy, rx, ry, start + i * 15, rotDeg));
  return smooth(pts, true);
}

export const circle = (cx: number, cy: number, r: number, start = -90) => ellipse(cx, cy, r, r, 0, start);

/** Rounded rectangle (closed), drawn clockwise from the top-left corner. */
export function rect(x: number, y: number, w: number, h: number, r = 0): string {
  if (r <= 0) return poly([[x, y], [x + w, y], [x + w, y + h], [x, y + h]], true);
  const q = Math.min(r, w / 2, h / 2);
  return (
    `M${fmt([x + q, y])}L${fmt([x + w - q, y])}Q${fmt([x + w, y])} ${fmt([x + w, y + q])}` +
    `L${fmt([x + w, y + h - q])}Q${fmt([x + w, y + h])} ${fmt([x + w - q, y + h])}` +
    `L${fmt([x + q, y + h])}Q${fmt([x, y + h])} ${fmt([x, y + h - q])}` +
    `L${fmt([x, y + q])}Q${fmt([x, y])} ${fmt([x + q, y])}Z`
  );
}

export const triangle = (a: Pt, b: Pt, c: Pt) => poly([a, b, c], true);

/** Regular polygon with `n` corners. */
export function polygon(cx: number, cy: number, r: number, n: number, rotDeg = -90): string {
  const pts: Pt[] = [];
  for (let i = 0; i < n; i++) pts.push(ellipsePoint(cx, cy, r, r, rotDeg + (360 * i) / n));
  return poly(pts, true);
}

/** Star with `n` points. */
export function star(cx: number, cy: number, rOuter: number, rInner: number, n = 5, rotDeg = -90): string {
  const pts: Pt[] = [];
  for (let i = 0; i < n * 2; i++) {
    const r = i % 2 === 0 ? rOuter : rInner;
    pts.push(ellipsePoint(cx, cy, r, r, rotDeg + (180 * i) / n));
  }
  return poly(pts, true);
}

/** Heart centred on (cx, cy) with total width `s`. */
export function heart(cx: number, cy: number, s: number): string {
  const h = s * 0.9;
  const top: Pt = [cx, cy - h * 0.25];
  const bottom: Pt = [cx, cy + h * 0.5];
  return (
    `M${fmt(top)}C${fmt([cx - s * 0.1, cy - h * 0.55])} ${fmt([cx - s * 0.55, cy - h * 0.45])} ${fmt([cx - s * 0.5, cy - h * 0.1])}` +
    `C${fmt([cx - s * 0.45, cy + h * 0.15])} ${fmt([cx - s * 0.15, cy + h * 0.3])} ${fmt(bottom)}` +
    `C${fmt([cx + s * 0.15, cy + h * 0.3])} ${fmt([cx + s * 0.45, cy + h * 0.15])} ${fmt([cx + s * 0.5, cy - h * 0.1])}` +
    `C${fmt([cx + s * 0.55, cy - h * 0.45])} ${fmt([cx + s * 0.1, cy - h * 0.55])} ${fmt(top)}Z`
  );
}

/** Teardrop / leaf shape from base point, pointing at `angleDeg`, with length and width. */
export function leaf(x: number, y: number, len: number, wid: number, angleDeg = -90): string {
  const tip = ellipsePoint(x, y, len, len, angleDeg);
  const mid = ellipsePoint(x, y, len * 0.5, len * 0.5, angleDeg);
  const left = ellipsePoint(mid[0], mid[1], wid / 2, wid / 2, angleDeg - 90);
  const right = ellipsePoint(mid[0], mid[1], wid / 2, wid / 2, angleDeg + 90);
  return `M${fmt([x, y])}Q${fmt(left)} ${fmt(tip)}Q${fmt(right)} ${fmt([x, y])}Z`;
}

/** Sine wave from x1 to x2 at height y. */
export function wave(x1: number, y: number, x2: number, amp: number, periods = 2): string {
  const n = Math.max(8, periods * 8);
  const pts: Pt[] = [];
  for (let i = 0; i <= n; i++) {
    const tt = i / n;
    pts.push([x1 + (x2 - x1) * tt, y + Math.sin(tt * periods * Math.PI * 2) * amp]);
  }
  return smooth(pts);
}

/** Zig-zag from x1 to x2 at height y. */
export function zigzag(x1: number, y: number, x2: number, amp: number, teeth = 6): string {
  const pts: Pt[] = [];
  for (let i = 0; i <= teeth * 2; i++) pts.push([x1 + ((x2 - x1) * i) / (teeth * 2), y + (i % 2 ? -amp : amp)]);
  return poly(pts);
}

/** Archimedean spiral. */
export function spiral(cx: number, cy: number, r: number, turns = 3): string {
  const pts: Pt[] = [];
  const n = turns * 16;
  for (let i = 0; i <= n; i++) {
    const tt = i / n;
    const a = tt * turns * Math.PI * 2;
    pts.push([cx + Math.cos(a) * r * tt, cy + Math.sin(a) * r * tt]);
  }
  return smooth(pts);
}

/** Several separate sub-paths joined into one stroke (drawn in order). */
export const join = (...ds: string[]) => ds.filter(Boolean).join(' ');

/**
 * Parallel hatch lines clipped to an ellipse. `angleDeg` is the line direction, `gap` the spacing.
 * Lines alternate direction so the animated pencil zig-zags like a real hand.
 */
export function hatchEllipse(cx: number, cy: number, rx: number, ry: number, angleDeg = 45, gap = 8): string {
  const a = rad(angleDeg);
  const dir: Pt = [Math.cos(a), Math.sin(a)];
  const nrm: Pt = [-dir[1], dir[0]];
  const reach = Math.max(rx, ry);
  const segs: string[] = [];
  let flip = false;
  for (let o = -reach; o <= reach; o += gap) {
    // Line: p = c + nrm*o + dir*s. Solve ((px-cx)/rx)^2 + ((py-cy)/ry)^2 = 1 for s.
    const ox = nrm[0] * o;
    const oy = nrm[1] * o;
    const A = (dir[0] / rx) ** 2 + (dir[1] / ry) ** 2;
    const B = 2 * ((ox * dir[0]) / rx ** 2 + (oy * dir[1]) / ry ** 2);
    const C = (ox / rx) ** 2 + (oy / ry) ** 2 - 1;
    const disc = B * B - 4 * A * C;
    if (disc <= 0) continue;
    const s1 = (-B - Math.sqrt(disc)) / (2 * A);
    const s2 = (-B + Math.sqrt(disc)) / (2 * A);
    const p1: Pt = [cx + ox + dir[0] * s1, cy + oy + dir[1] * s1];
    const p2: Pt = [cx + ox + dir[0] * s2, cy + oy + dir[1] * s2];
    segs.push(flip ? line(p2[0], p2[1], p1[0], p1[1]) : line(p1[0], p1[1], p2[0], p2[1]));
    flip = !flip;
  }
  return segs.join(' ');
}

/** Hatching clipped to an axis-aligned rectangle. */
export function hatchRect(x: number, y: number, w: number, h: number, angleDeg = 45, gap = 8): string {
  const a = rad(angleDeg);
  const dir: Pt = [Math.cos(a), Math.sin(a)];
  const nrm: Pt = [-dir[1], dir[0]];
  const cx = x + w / 2;
  const cy = y + h / 2;
  const reach = Math.hypot(w, h) / 2;
  const segs: string[] = [];
  let flip = false;
  for (let o = -reach; o <= reach; o += gap) {
    const px = cx + nrm[0] * o;
    const py = cy + nrm[1] * o;
    // Clip parametric line p + dir*s against the rect (Liang-Barsky).
    let s0 = -Infinity;
    let s1 = Infinity;
    const clip = (p: number, d: number, lo: number, hi: number) => {
      if (Math.abs(d) < 1e-9) return p >= lo && p <= hi;
      let a1 = (lo - p) / d;
      let a2 = (hi - p) / d;
      if (a1 > a2) [a1, a2] = [a2, a1];
      s0 = Math.max(s0, a1);
      s1 = Math.min(s1, a2);
      return true;
    };
    if (!clip(px, dir[0], x, x + w) || !clip(py, dir[1], y, y + h) || s0 >= s1) continue;
    const p1: Pt = [px + dir[0] * s0, py + dir[1] * s0];
    const p2: Pt = [px + dir[0] * s1, py + dir[1] * s1];
    segs.push(flip ? line(p2[0], p2[1], p1[0], p1[1]) : line(p1[0], p1[1], p2[0], p2[1]));
    flip = !flip;
  }
  return segs.join(' ');
}

/** Short "fur" flicks along an elliptical arc, pointing outward. */
export function furArc(cx: number, cy: number, rx: number, ry: number, a0: number, a1: number, count = 10, len = 8): string {
  const segs: string[] = [];
  for (let i = 0; i < count; i++) {
    const deg = a0 + ((a1 - a0) * (i + 0.5)) / count;
    const p = ellipsePoint(cx, cy, rx, ry, deg);
    const q = ellipsePoint(cx, cy, rx + len, ry + len, deg + (i % 2 ? 4 : -4));
    segs.push(line(p[0], p[1], q[0], q[1]));
  }
  return segs.join(' ');
}

/** Small dots (stipple / texture), deterministic for a given seed. */
export function dots(x: number, y: number, w: number, h: number, count: number, size = 1.5, seed = 7): string {
  let s = seed;
  const rnd = () => ((s = (s * 16807) % 2147483647) - 1) / 2147483646;
  const out: string[] = [];
  for (let i = 0; i < count; i++) out.push(circle(x + rnd() * w, y + rnd() * h, size));
  return out.join(' ');
}

/** Flower petals around a centre. */
export function petals(cx: number, cy: number, n: number, len: number, wid: number, rotDeg = -90): string {
  const out: string[] = [];
  for (let i = 0; i < n; i++) out.push(leaf(cx, cy, len, wid, rotDeg + (360 * i) / n));
  return out.join(' ');
}

/** Lines radiating from a centre (sun rays, whiskers, sparkles). */
export function rays(cx: number, cy: number, r0: number, r1: number, n: number, a0 = 0, a1 = 360): string {
  const out: string[] = [];
  const full = Math.abs(a1 - a0) >= 360;
  for (let i = 0; i < n; i++) {
    const deg = a0 + ((a1 - a0) * (full ? i : i + 0.5)) / n;
    const p = ellipsePoint(cx, cy, r0, r0, deg);
    const q = ellipsePoint(cx, cy, r1, r1, deg);
    out.push(line(p[0], p[1], q[0], q[1]));
  }
  return out.join(' ');
}

/** Mirror points horizontally around x = axis (for symmetric faces / bodies). */
export const mirrorX = (pts: Pt[], axis: number): Pt[] => pts.map(([x, y]) => [2 * axis - x, y] as const);

/** Translate + uniform scale a list of points. */
export const place = (pts: Pt[], dx: number, dy: number, s = 1): Pt[] => pts.map(([x, y]) => [x * s + dx, y * s + dy] as const);

/**
 * Grid guide (light construction lines) for the reference / proportion lessons.
 */
export function grid(x: number, y: number, w: number, h: number, cols: number, rows: number): string {
  const out: string[] = [];
  for (let i = 0; i <= cols; i++) out.push(line(x + (w * i) / cols, y, x + (w * i) / cols, y + h));
  for (let j = 0; j <= rows; j++) out.push(line(x, y + (h * j) / rows, x + w, y + (h * j) / rows));
  return out.join(' ');
}

/** Rough bounding box of path data (numbers only; good enough for validation and framing). */
export function bbox(d: string): { x: number; y: number; w: number; h: number } | null {
  const nums = (d.match(/-?\d+(?:\.\d+)?/g) ?? []).map(Number);
  if (nums.length < 2) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (let i = 0; i + 1 < nums.length; i += 2) {
    minX = Math.min(minX, nums[i]!);
    maxX = Math.max(maxX, nums[i]!);
    minY = Math.min(minY, nums[i + 1]!);
    maxY = Math.max(maxY, nums[i + 1]!);
  }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}
